import clsx from 'clsx'
import React, { useMemo, useRef, useState } from 'react'
import { Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import 'swiper/css'
import arrow from 'assets/images/slider/arrow.svg'

import styles from './Slider.module.scss'

import 'swiper/scss/navigation'
import 'swiper/scss/pagination'

interface Props {
  items: React.ReactNode[]
  slidesPerView?: number | 'auto'
  className?: string
  wrapperClassName?: string
  id: string
  spacing?: number
  paginationUpdateAction?: (page: number) => void
  navigationNextAction?: (page: number) => void
  navigationPrevAction?: (page: number) => void
}

const Slider = React.memo(
  ({
    items,
    slidesPerView,
    className,
    wrapperClassName,
    id,
    spacing = 40,
    paginationUpdateAction,
    navigationNextAction,
    navigationPrevAction,
  }: Props) => {
    const slides = useMemo(() => {
      return React.Children?.map(items, slide => <SwiperSlide className={styles.swiperSlide}>{slide}</SwiperSlide>)
    }, [items])

    const swiperRef = useRef(null)

    const [isBeginning, setIsBeginning] = useState(true)
    const [isEnd, setIsEnd] = useState(false)

    const handleSlideChange = (swiperInstance: any) => {
      setIsBeginning(swiperInstance?.isBeginning)
      setIsEnd(swiperInstance?.isEnd)
    }

    return (
      <div className={clsx('w-full relative', styles.swiperWrapper, wrapperClassName)}>
        <div
          className={clsx(`prev-arrow-${id} absolute left-4 top-1/2 transition-all`, styles.arrow, styles.prev, {
            [styles.disabled]: isBeginning,
          })}
        >
          <img src={arrow} alt="prev-arrow" />
        </div>
        <Swiper
          ref={swiperRef}
          className={clsx(className)}
          navigation={{
            prevEl: `.prev-arrow-${id}`,
            nextEl: `.next-arrow-${id}`,
          }}
          modules={[Navigation, Pagination]}
          pagination={{
            clickable: true,
            horizontalClass: styles.swiperPaginationHorizontal,
            bulletClass: styles.swiperPaginationBullet,
            bulletActiveClass: styles.swiperPaginationBulletActive,
          }}
          slidesPerView={slidesPerView}
          spaceBetween={spacing}
          onSlideChange={handleSlideChange}
          onSwiper={swiperInstance => {
            handleSlideChange(swiperInstance)

            /* Swiper does not provide callbacks for clicking on pagination bullets, 
            therefore this is a hackish way to detect clicks */

            swiperInstance.el.addEventListener('click', (e: any) => {
              if (e.target.classList.contains(styles.swiperPaginationBullet)) {
                const clickedBulletIndex = Array.from(e.target.parentElement.children).indexOf(e.target)
                paginationUpdateAction && paginationUpdateAction(clickedBulletIndex)
              }
            })
          }}
          onNavigationNext={({ realIndex }) => navigationNextAction && navigationNextAction(realIndex)}
          onNavigationPrev={({ realIndex }) => navigationPrevAction && navigationPrevAction(realIndex)}
        >
          {slides}
        </Swiper>
        <div
          className={clsx(`next-arrow-${id} absolute right-4 top-1/2 transition-all`, styles.arrow, styles.next, {
            'opacity-0': isEnd,
          })}
        >
          <img src={arrow} alt="next-arrow" />
        </div>
      </div>
    )
  },
)

export default Slider
